// src/app/services/press-release.service.ts

import { Injectable } from '@angular/core';
import {
  createPressReleaseSlug,
  PressRelease
} from '../models/press-release';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PressReleaseService {

  private pressReleases: PressRelease[] = [

    {
      id: '1',
      title: 'Lydia Christensen Announces Candidacy for U.S. Senate from Michigan in 2026 Election',
      date: new Date(2025, 7, 1), // August 1, 2025
      summary: 'Independent Candidate to Unchain Michigan’s Future with Bold Vision for Environment, Economy, and Equality',
      content: `
        Lydia Christensen, a proud Michigander, community advocate, and member of the LGBTQIA+ community, today announced her independent candidacy for U.S. Senator from Michigan in the 2026 election. With a platform centered on protecting the Great Lakes, fostering a sustainable economy, and defending the rights of all Michiganders, Lydia aims to unchain Michigan’s potential through bold, people-focused leadership.

        Lydia’s campaign is built on a vision to secure Michigan’s environmental and economic future while championing equality and fairness. She will advocate for federal investments to protect the Great Lakes, which hold 20% of the world’s freshwater, and promote clean energy projects like wind, solar, and hydropower to create jobs and combat climate change.

        Her economic plan includes:
        - Replacing outdated industries with sustainable ones.
        - Securing federal incentives for green manufacturing.
        - Expanding fiber optic broadband to rural areas like the Upper Peninsula.

        As a fierce defender of rights, Lydia will:
        - Protect Social Security for the next 100 years by closing tax loopholes for the ultra-wealthy, ensuring seniors enjoy dignified retirements without poverty.
        - Raise the federal minimum wage to $15 per hour.
        - Strengthen voting rights to ensure every Michigander’s voice is heard.

        Lydia will champion inclusive marriage equality, defend LGBTQIA+ civil rights, and promote diversity, equity, and inclusion through federal policies that uplift underserved communities. She supports federal legalization of marijuana to drive social equity and economic growth, alongside legislation to foster Bitcoin and cryptocurrency innovation with consumer protections.

        On the global stage, Lydia will push for U.S. leadership in fighting climate change, countering authoritarianism, and ending reckless foreign wars through diplomacy. She will uphold the U.S. Constitution as the bedrock of Michigan’s freedoms, ensuring equal protection and due process for all.

        > **“I’m running to unchain Michigan’s future, a future where clean water, good jobs, and equal rights are guaranteed for every Michigander,”** said Lydia Christensen. **“As an independent, I’ll work across aisles to deliver results, not rhetoric, for Detroit, the Upper Peninsula, and everywhere in between.”**

        > **“We need a government that serves people, not special interests,”** Christensen added. **“Social Security is a promise to our seniors, not an entitlement. I’ll propose legislation to fully fund it for a century, so no Michigander faces poverty in their golden years.”**

        Lydia invites Michiganders to join her movement at [www.LydiaLynnChristensen.com](https://www.LydiaLynnChristensen.com). Follow the campaign on X ([@Lydia4USSenate](https://x.com/Lydia4USSenate)), Facebook ([@LydiaForUSSenate](https://facebook.com/LydiaForUSSenate)), and TikTok ([@LydiaForUSSenate](https://tiktok.com/@LydiaForUSSenate)).

        ### About Lydia Christensen

        Lydia Christensen is an LGBTQ+ Michigander and passionate advocate for environmental protection, economic opportunity, and equality. Her independent campaign for U.S. Senate focuses on sustainable growth, healthcare access, and defending the rights of all Michiganders. Learn more at [www.LydiaLynnChristensen.com](https://www.LydiaLynnChristensen.com).

        ### Media Contact

        Lydia Christensen
        (906) 675-9716
        [senator@lydialynnchristensen.com](mailto:senator@lydialynnchristensen.com)
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '2',
      title: "Christensen Vows to End the Silence: 'Our Ancestors’ Truth Is Not Shoddy or For Sale'",
      date: new Date(2026, 1, 9), // February 9, 2026
      summary: 'U.S. Senate candidate demands immediate release of $1.1M Native American boarding school report following allegations of whitewashing.',
      content: `
FOR IMMEDIATE RELEASE
Date: February 9, 2026
Contact: Lydia Christensen | senator@lydialynnchristensen.com

Lydia Christensen, candidate for U.S. Senate, stood in solidarity with Michigan’s Tribal Nations to demand the immediate and unredacted release of a $1.1 million state-commissioned report documenting abuses at Native American boarding schools.

The report, commissioned by Michigan’s Department of Civil Rights (MDCR), has reportedly been shelved despite containing survivor testimony and research into the deaths of hundreds of children. Christensen, who has Native American ancestry, condemned the decision as a profound act of disrespect toward Elders and survivors who courageously shared their experiences.

> “When the state dismisses the stories of survivors as shoddy, they are not critiquing a report, they are dismissing the pain of our ancestors,” said Christensen. “I am standing for what is right. The truth is not merely a budget failure; it is an attempt to rewrite history to protect government bureaucracy.”

According to allegations from the research firm Kauligan and Associates, state officials instructed them that this must be corrected immediately.

## Christensen’s Plan for Truth and Accountability

**Immediate Public Release:** The full $350-page manuscript must be released to Tribal Nations and the public without further delay.

**Justice for the Fallen:** Conduct a full investigation into the 229 documented deaths at the Mt. Pleasant Indian Industrial Boarding School.

**Federal Intervention:** Christensen has formally petitioned the U.S. Senate Committee on Indian Affairs to investigate the suppression and alteration of these records.

> “I am running for the U.S. Senate to be a voice for those the government has tried to silence for more than a century,” Christensen added. “There can be no reconciliation without truth. I will not stop until every survivor’s story is honored and every buried file is brought into the light.”

### About Lydia Christensen

Lydia Christensen is an advocate for the rights of Michigan’s Tribal Nations. She carries Native American ancestry and has long defended accountability and integrity in government.
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '3',
      title: 'Christensen Demands Reinstatement of Pride Flag at Stonewall National Monument',
      date: new Date(2026, 1, 10), // February 10, 2026
      summary: 'U.S. Senate candidate calls on Interior Secretary and National Park Service to immediately restore the Rainbow Pride Flag at Stonewall National Monument.',
      content: `
FOR IMMEDIATE RELEASE
Date: February 10, 2026

To: Secretary Doug Burgum, Department of the Interior
Cc: Jessica Bowen, Director of the National Park Service
From: The Campaign of Lydia Christensen for U.S. Senate
Re: Reinstatement of the Pride Flag at Stonewall National Monument

Secretary Burgum,

I am writing to formally demand the immediate reinstatement of the Rainbow Pride Flag at Stonewall National Monument in New York City. The recent removal of this flag under the guise of "consistency" is not a neutral administrative act; it is a deliberate attempt to erase the history and identity of the LGBTQ+ community.

From the very site that catalyzed their movement for civil rights, Stonewall is a unique landmark. It does not exist to celebrate a military victory or a distant historical figure; it exists to honor rebellion for dignity, equality, and the right to exist without fear of state-sponsored retaliation.

The Rainbow Flag is the international symbol of that struggle. To remove it from Stonewall is as illogical and historically inconsistent as removing the American flag from a site commemorating the Revolution. The Department's own guidance allows for "limited exceptions" for flags that provide historical context. There is no site in the National Park System where a non-agency flag is more contextually or historically vital than at Stonewall.

As a candidate for the U.S. Senate in Michigan, I believe in a government that is transparent, inclusive, and respectful of the diverse stories that make up the American tapestry. Erasure is not leadership.

I urge you to exercise your authority to grant a historical exception for the Stonewall National Monument and return the Pride Flag to its rightful place immediately.

Sincerely,
Lydia Christensen
Candidate for U.S. Senate, Michigan
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '4',
      title: "Christensen Blasts Federal 'Citizenship Quotas,' Announces Michigan Citizenship Security Plan",
      date: new Date(2026, 1, 13), // February 13, 2026
      summary: 'U.S. Senate candidate condemns reported federal denaturalization quotas and pledges to defund offices using numerical targets to strip Americans of citizenship.',
      content: `
FOR IMMEDIATE RELEASE
Date: February 13, 2026
Contact: Press Office, Lydia Christensen for U.S. Senate

Ironwood, MI — Lydia Christensen, independent candidate for the U.S. Senate, today condemned new federal directives forcing U.S. Citizenship and Immigration Services (USCIS) to meet monthly "denaturalization quotas." Reporting confirms field offices are now pressured to refer 100 to 200 cases monthly to the Department of Justice, a twentyfold increase over historical norms.

> "Citizenship is the foundation of our Republic. It is not a subscription service that the government can cancel to meet a monthly quota," said Christensen. "By ordering bureaucrats to 'find' hundreds of cases to justify their budgets, this administration is treating 400,000 naturalized Michiganders like suspects instead of citizens. We will not allow a two-tier system of American rights to take root in our state."

> "This is a 'Power of the Purse' issue," Christensen continued. "The Executive Branch is weaponizing taxpayer dollars to build a pipeline for judicial harassment. As your Senator, I will move to defund any office that utilizes numerical targets for stripping Americans of their status."

### About Lydia Christensen

Lydia Christensen is an independent candidate for U.S. Senate in Michigan focused on protecting constitutional rights, strengthening democratic institutions, and ensuring equal treatment under the law for all Americans.
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '5',
      title: "Christensen Calls for 'Federal Restraint' in Michigan School Investigations",
      date: new Date(2026, 1, 18), // February 18, 2026
      summary: 'U.S. Senate candidate urges DOJ to halt compliance reviews in Michigan districts, citing constitutional overreach and 10th Amendment protections.',
      content: `
FOR IMMEDIATE RELEASE
Contact: Lydia Christensen | senator@lydialynnchristensen.com

Ironwood, MI — Lydia Christensen, Independent candidate for the United States Senate, today called for an immediate halt to federal "fishing expeditions" in Michigan school districts following the Department of Justice’s announcement of compliance reviews in Lansing, Detroit, and Godfrey-Lee.

The DOJ launched these investigations on February 18, 2026, citing Title IX and the Supreme Court’s 2025 Mahmoud v. Taylor decision. Christensen, a technology CEO and constitutional advocate, argues that the move is an unnecessary escalation of federal power into local affairs.

> "Michigan is currently caught in a legal whiplash between conflicting federal rulings," said Christensen. "While D.C. lawyers argue over memos and mandates, our local districts are being subjected to invasive investigations. As your next Senator, I will fight to ensure that the federal government respects the 10th Amendment. Education is a local responsibility, and our school boards are accountable to Michigan voters, not federal agencies."

## Christensen’s Education Governance Platform

**Constitutional Autonomy:** Reaffirming that education is not a power delegated to the federal government.

**Parental Rights:** Strengthening the partnership between parents and local boards through transparency, not federal coercion.

**Ending Administrative Overreach:** Opposing the use of federal agencies to bypass local democratic processes.

> "We don't need Washington to supervise our schools," Christensen added. "We need a Senator who trusts Michigan’s parents and teachers."

### Media Contact

Lydia Christensen
senator@lydialynnchristensen.com

www.lydialynnchristensen.com
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '6',
      title: "Christensen Denounces IOC's Mandatory Gene Testing Policy for 2028 LA Olympics",
      date: new Date(2026, 2, 28), // March 28, 2026
      summary: "U.S. Senate candidate calls IOC's mandatory SRY gene testing for female athletes a violation of bodily autonomy and biological privacy, pledges congressional oversight of USOPC.",
      content: `
FOR IMMEDIATE RELEASE
Contact: Lydia Christensen | senator@lydialynnchristensen.com

**Lydia Christensen Denounces IOC's Mandatory Gene Testing Policy for 2028 LA Olympics; Calls for Protection of Athlete Privacy and Dignity**

Ironwood, MI — Today, U.S. Senate candidate Lydia Christensen issued a sharp rebuke of the International Olympic Committee's (IOC) new mandate requiring all female athletes to undergo genetic screening for the 2028 Los Angeles Summer Games.

> "The IOC's decision to implement mandatory SRY gene testing is a regressive step that violates the fundamental right to bodily autonomy and biological privacy," said Lydia Christensen. "As we prepare to host the world in Los Angeles, the United States must lead with values of inclusion and respect, not by subjecting women to invasive 'gender policing' that has historically targeted Black and Brown athletes and those with natural biological variations."

Lydia Christensen highlighted several key concerns regarding the policy:

**Privacy Risks:** Mandatory genetic testing requires the collection of sensitive biometric data, raising massive security concerns for American athletes.

**Discriminatory Impact:** Decades of "sex testing" in sports have disproportionately impacted women from the Global South and women of color whose bodies do not fit narrow, Western stereotypes of femininity.

**Federal Oversight:** Under the Empowering Olympic, Paralympic, and Amateur Athletes Act of 2020, Congress has the authority to oversee the USOPC. Lydia Christensen pledged that, if elected, she would use this oversight to ensure the USOPC does not comply with international mandates that violate the civil rights of American athletes.

> "We cannot allow a private international body to demand the genetic blueprints of our citizens as a 'pay-to-play' requirement for the Olympics," Lydia Christensen continued. "I am calling on the USOPC to stand with our athletes and reject this scientifically flawed and ethically bankrupt policy."

### About Lydia Christensen

Lydia Christensen is an independent candidate for U.S. Senate in Michigan focused on protecting civil rights, bodily autonomy, and the dignity of all Americans. Learn more at https://www.lydialynnchristensen.com.

### Media Contact

Lydia Christensen
senator@lydialynnchristensen.com
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '8',
      title: 'Christensen Demands FCC Commission Go On Record, Calls for Senate Hearing on Coordinated Campaign Against ABC and Disney',
      date: new Date(2026, 4, 14), // May 14, 2026
      summary: 'FCC Commissioner Anna Gomez Confirms What Christensen Campaign Warned Weeks Ago: Federal Licensing Power Being Used to Silence a Free Press',
      content: `
FOR IMMEDIATE RELEASE
May 14, 2026
Contact: Senator@lydialynnchristensen.com | (906) 675-9716

![FCC Press Release](assets/FCCPressRelease.jpg)

MICHIGAN — Lydia Christensen, independent candidate for United States Senate, today issued a formal demand that the full Federal Communications Commission go on record publicly regarding the use of early broadcast license renewal as a pressure mechanism against Disney and its ABC-owned stations, and called on the Senate Commerce Committee to convene an immediate oversight hearing.

FCC Commissioner Anna Gomez published a public letter this week describing what she called a deliberate, coordinated government campaign against Disney and ABC. Gomez identified the FCC's order requiring Disney's eight ABC-owned stations to seek early license renewal — a mechanism unused for more than 50 years — as censorship dressed up as regulation.

> "We raised this alarm weeks ago," said Christensen. "A broadcast license is a lifeline. No license, no signal, no network. When the federal government uses regulatory power to threaten that lifeline, it is not enforcing policy. It is sending a message to every newsroom in America: fall in line or we come for you next. Commissioner Gomez just confirmed from inside the agency what we were already saying from outside it."

Christensen is calling for two immediate actions:

1. The full FCC commission must go on record publicly — each commissioner must state whether they support or oppose the use of early license renewal as a pressure mechanism against broadcasters. Silence is complicity.

2. The Senate Commerce Committee, which holds FCC oversight jurisdiction, must convene an immediate public hearing. The American people deserve to know whether their federal communications regulator is being used as a political weapon against the free press.

The First Amendment does not expire when a network's license comes up for renewal. This is not a regulatory coincidence. This is a pattern. And it will not stop unless the American people demand it stops.

Christensen's campaign has consistently championed a Digital Bill of Rights as a constitutional firewall against government overreach into speech, broadcasting, and digital infrastructure. More information is available at https://www.lydialynnchristensen.com.

### About Lydia Christensen

Lydia Christensen is an independent candidate for United States Senate in Michigan, running under the Unchaining brand. Her platform centers on civil liberties, a Digital Bill of Rights, voting rights, clean energy, and constitutional accountability.

### Media Contact

senator@lydialynnchristensen.com
(906) 675-9716
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '9',
      title: "Senate Candidate Lydia Christensen Calls on Governor Whitmer to Request Federal CRIPA Investigation into Women's Huron Valley Correctional Facility",
      date: new Date(2026, 5, 9), // June 9, 2026
      summary: "Independent U.S. Senate candidate calls on Governor Whitmer to request a DOJ civil rights investigation following three inmate deaths in less than a month at Michigan's only women's prison.",
      content: `
![Senate Candidate Lydia Christensen — CRIPA Press Release](assets/HuronValleyPressRelease.jpg)

FOR IMMEDIATE RELEASE
June 9, 2026
Contact: Lydia Christensen
Senator@lydialynnchristensen.com
lydiaforsenate.com

IRONWOOD, MI — Independent U.S. Senate candidate Lydia Christensen today sent a formal letter to Governor Gretchen Whitmer calling on her to request a federal civil rights investigation into Women's Huron Valley Correctional Facility following three inmate deaths in less than a month.

Khaira Howard, 28, died May 13. Rebecca Fackler, 57, died May 17. Ashley Hoath, 36, died June 6. Attorneys handling civil cases for the families of Howard and Fackler allege both women were denied medical care before their deaths. Inmates near Hoath say they spent three hours urging guards to check on her before anyone acted. She had graduated from Jackson College while incarcerated less than two months before her death.

MDOC has not released an environmental assessment of the facility conducted in response to mold concerns. On June 8, U.S. Rep. Debbie Dingell wrote to Governor Whitmer demanding that any studies, inspections, environmental assessments, or reports concerning toxic mold and other health and safety conditions at the facility be released to the public.

Christensen is calling on Whitmer to formally request a Department of Justice investigation under the Civil Rights of Institutionalized Persons Act, which authorizes the Attorney General to investigate state prisons where a pattern or practice of constitutional violations is causing grievous harm to residents.

> "Three deaths in three weeks is not a coincidence. It is a pattern," Christensen said. "Governor Whitmer has the standing to request federal oversight today and she should use it. If she acts, Michigan women are safer. If she doesn't, Michigan voters will know she had the power and chose not to use it."

Thirty Michigan lawmakers have called for MDOC Director Heidi Washington's resignation. Christensen argues resignation alone is insufficient without structural federal oversight to identify root causes and produce enforceable remedies.

Members of the public can also file CRIPA complaints directly with the DOJ Civil Rights Division at https://www.justice.gov/crt/how-file-complaint.

The full letter to Governor Whitmer is available at https://www.lydialynnchristensen.com.

### About Lydia Christensen

Lydia Christensen is an independent candidate for the United States Senate in Michigan running under the Unchaining Michigan platform. She is based in Ironwood in the Upper Peninsula.

### Media Contact

Lydia Christensen
senator@lydialynnchristensen.com

---

## Full Letter to Governor Whitmer

Governor Whitmer and Press Office,

I am writing as an independent candidate for the United States Senate in Michigan to formally request that Governor Whitmer use her standing to request a federal Department of Justice investigation into Women's Huron Valley Correctional Facility under the Civil Rights of Institutionalized Persons Act (CRIPA).

Three women have died at Michigan's only women's prison in less than a month. Khaira Howard, 28, died May 13. Rebecca Fackler, 57, died May 17. Ashley Hoath, 36, died June 6. Attorneys representing the families of Howard and Fackler allege both women were denied medical care before their deaths. Inmates report spending three hours urging guards to check on Hoath before anyone responded.

Thirty Michigan lawmakers have called for MDOC Director Heidi Washington's resignation. I agree that change in leadership is necessary. It is not sufficient. Resignation alone does not identify root causes, does not produce enforceable remedies, and does not prevent the next death. A federal CRIPA investigation does.

CRIPA authorizes the Attorney General to investigate state prisons where a pattern or practice of constitutional violations is causing grievous harm to residents. Governor Whitmer can formally request that investigation today. I am calling on her to do so.

A copy of my formal letter on this matter is attached. It has also been released publicly and distributed to Michigan media.

Respectfully,

Lydia Christensen
Independent Candidate, United States Senate — Michigan
Unchaining Michigan
Senator@lydialynnchristensen.com
lydiaforsenate.com
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '7',
      title: 'Christensen Demands DOJ Withdraw Wayne County Ballot Request and Drop Michigan Voter Roll Lawsuit',
      date: new Date(2026, 3, 24), // April 24, 2026
      summary: "U.S. Senate candidate calls DOJ's April 14 demand for Wayne County ballots unconstitutional, citing Article I, Section 4 and the federal government's fundamental misunderstanding of Michigan's ballot custody structure.",
      content: `
FOR IMMEDIATE RELEASE
Date: April 24, 2026
Contact: Lydia Christensen | senator@lydialynnchristensen.com

![Official Letter — Demand for Withdrawal of Wayne County Ballot Request](assets/wayne-county-ballot-letter.jpg)

**Assistant Attorney General Harmeet Dhillon**
Civil Rights Division
U.S. Department of Justice
950 Pennsylvania Avenue NW
Washington, D.C. 20530

**Re: Demand for Withdrawal of Wayne County Ballot Request and Dismissal of Michigan Voter Roll Lawsuit**

Dear Assistant Attorney General Dhillon,

I am writing as a candidate for the United States Senate from the state of Michigan and as a citizen who has spent the last twenty-one days tracking the systematic effort by the Trump administration to assert federal control over state-administered elections. I am calling on you to immediately withdraw the Department of Justice's April 14 demand for Wayne County ballots and to drop the federal lawsuit against the state of Michigan over its voter rolls.

The Constitution is unambiguous. Article I, Section 4 grants states primary authority over the administration of elections. The Department of Justice's demand is not a law passed by Congress. It is a unilateral executive branch action with no constitutional foundation. The letter you signed on April 14 demanded ballots from Wayne County — an entity that does not possess them. In Michigan, ballots are held by 43 individual local clerks. The federal government sent a demand to the wrong office. That is not an oversight. It is a pattern.

Sincerely,
Lydia Christensen
Candidate for U.S. Senate, Michigan

---

### Media Contact

Lydia Christensen
senator@lydialynnchristensen.com
www.lydialynnchristensen.com
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '10',
      title: 'On America’s 250th Independence Day, Lydia Christensen Confirmed as Libertarian Nominee for U.S. Senate in Michigan',
      date: new Date(2026, 6, 4), // July 4, 2026
      summary: "The Libertarian Party of Michigan has certified Christensen's nomination for the November 3, 2026 general election following the state convention held June 28 in Troy.",
      content: `
![Lydia Christensen — Candidate for United States Senator, Libertarian](assets/LibertarianNomineePressRelease.png)

FOR IMMEDIATE RELEASE
July 4, 2026

IRONWOOD, Mich., July 4, 2026. On the 250th anniversary of the Declaration of Independence, Lydia Christensen, candidate for U.S. Senate, has been confirmed as the Libertarian Party of Michigan's nominee for the November 3, 2026 general election. The Libertarian Party of Michigan certified her nomination following the state convention held June 28 in Troy.

Christensen has run her campaign under the banner "Unchaining Michigan" for more than a year, building a platform centered on civil liberties and economic populism. Her positions include repeal of the Patriot Act and FISA Section 702, removal of automated license plate reader surveillance networks, a Digital Bill of Rights that enumerates a person's personal identifying information as their own property, and Medicaid for All.

> "Two hundred fifty years ago, people risked everything over the idea that government needs consent and government needs limits," said Christensen. "Both major parties have had thirty years to produce meaningful anti-surveillance legislation, and both have consistently voted against the Constitution's interests instead. Section 702 of FISA still lets the government search Americans' communications without a warrant in 2026, and neither party has fixed it. That is the actual measure of whether this country still believes what it said in 1776. Not a slogan. A warrant requirement. My name is going to be on the ballot in November so Michigan voters have someone running who will hold that line instead of just quoting the Declaration on the Fourth of July and voting the other way in December."

Christensen is a member of the Kiowa-Sioux Nation and has connected her campaign's civil liberties platform to tribal sovereignty and federal treaty obligations. She lives in Ironwood, in Michigan's Upper Peninsula. Christensen's committee, Lydia Christensen for United States Senate, is registered with the Federal Election Commission under Committee ID C00903575.

Those who would like to support the campaign can donate at https://www.lydialynnchristensen.com.

### Media Contact

Lydia Christensen
senator@lydialynnchristensen.com

### About the Campaign

Lydia Christensen for United States Senate is a Michigan-based campaign built on people power, standing up for our civil liberties, and ensuring America stands for another 250 years. Learn more at https://www.lydialynnchristensen.com.
      `,
      link: 'https://www.lydialynnchristensen.com'
    },

    {
      id: '11',

      // Permanent canonical URL.
      // This prevents the URL from changing if the headline
      // is edited after publication.
      slug: '2026-09-21-lydia-christensen-calls-white-house-press-ban-a-threat-to-press-freedom',

      title: 'Lydia Christensen Calls White House Press Ban a Threat to Press Freedom',

      image: 'assets/whitehousePressRelease-LydiaChristensen.png',

      date: new Date(2026, 8, 21), // September 21, 2026

      summary: 'U.S. Senate candidate Lydia Christensen calls for the restoration of White House press access for CNN, MS NOW and Politico and proposes stronger protections for journalists covering the federal government.',

      content: `
![Lydia Christensen Press Release — White House Press Freedom](assets/whitehousePressRelease-LydiaChristensen.png)

FOR IMMEDIATE RELEASE
September 21, 2026
Contact: Lydia Christensen | senator@lydialynnchristensen.com

IRONWOOD, MICHIGAN — U.S. Senate candidate Lydia Christensen is calling for the restoration of White House press access for CNN, MS NOW and Politico after reporters from the three organizations were denied entry and had their press credentials disabled or confiscated.

President Donald Trump announced the ban Friday, accusing the outlets of publishing what he called "fake news." The ban was enforced Saturday, when reporters for the three organizations were turned away from the White House. The outlets have now announced plans to sue the Trump administration, arguing that the restrictions violate their First Amendment rights.

> "The government should not be deciding which journalists are allowed to report from the White House based on whether the president likes their coverage," Christensen said. "The press does not work for the president. It works for the American people."

Christensen said the issue goes beyond CNN, MS NOW or Politico.

> "When the government can punish a news organization by taking away access because it doesn't like what that organization reports, every journalist has reason to be concerned. A free press has to be able to question the people in power. That includes presidents."

Christensen said that, if elected to the U.S. Senate, she would push for legislation establishing clearer protections for journalists covering the federal government. Her proposed reforms would:

- **Require clear standards:** Federal agencies that issue press credentials should publish clear standards for granting, denying, and revoking credentials.
- **Require due process:** Journalists should receive notice and an opportunity to respond before a press credential is revoked, except when an immediate security concern requires temporary action.
- **Prohibit retaliation:** Federal officials should not use press credentials, press pool assignments, or government access as retaliation for an outlet's editorial position or critical reporting.
- **Increase congressional oversight:** Congress should have greater oversight of federal press credentialing practices.

Christensen also said the Senate should examine the role of congressional press galleries in the federal press credentialing system and whether existing procedures provide sufficient transparency and accountability.

> "The First Amendment belongs to everyone," Christensen said. "It doesn't disappear when the person being questioned is the president."

### Media Availability

Lydia Christensen is available for satellite, in-studio, and phone interviews regarding First Amendment rights, press access, congressional oversight, and executive power.

To request an interview or additional information, contact the campaign press office at 906-675-9716 or senator@lydialynnchristensen.com.

### About Lydia Christensen for U.S. Senate

Lydia Christensen is a Libertarian candidate for the United States Senate from Ironwood, Michigan. Her campaign focuses on constitutional rights, individual liberty, government accountability, and limits on executive power.

Learn more at www.lydiaforsenate.com.

### Media Contact

Lydia Christensen
senator@lydialynnchristensen.com
(906) 675-9716
      `,

      link: 'https://www.lydialynnchristensen.com'
    }
  ];

  // Hard coded press releases; upgrade to using backend.

  constructor() {}

  // Get all press releases, sorted by date (newest first).
  getPressReleases(): Observable<PressRelease[]> {
    const sortedReleases = [...this.pressReleases].sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );

    return of(sortedReleases);
  }

  // Accept the canonical slug as well as a legacy numeric ID.
  getPressReleaseByIdentifier(
    identifier: string
  ): Observable<PressRelease | undefined> {

    const release = this.pressReleases.find(
      release =>
        release.id === identifier ||
        createPressReleaseSlug(release) === identifier
    );

    return of(release);
  }

  // Add a new press release.
  addPressRelease(
    release: PressRelease
  ): Observable<PressRelease> {

    const numericIds = this.pressReleases
      .map(release => Number(release.id))
      .filter(id => Number.isFinite(id));

    const nextId =
      numericIds.length > 0
        ? Math.max(...numericIds) + 1
        : 1;

    release.id = String(nextId);
    release.date = new Date(release.date);

    if (!release.slug) {
      release.slug =
        createPressReleaseSlug(release);
    }

    this.pressReleases.push(release);

    return of(release);
  }
}